import React from "react";

import { CouponTypeDto } from "services/coupons/restModels";

import {
    Banner,
    Button,
    DateField,
    LabeledField,
    Modal,
    Select,
    Stack,
    TextInput,
} from "shared/ui";

import { useCouponsContext } from "features/Coupons/context";
import { useCouponForm } from "features/Coupons/hooks/useCouponForm";

import { CodeField, CodeRow, Period } from "./styles";

const TYPE_OPTIONS = [
    { value: "Percentage", label: "Скидка в процентах" },
    { value: "FixedAmount", label: "Скидка суммой" },
];

/*
* Форма промокода. Монтируется только когда диалог открыт (см. корень фичи) — так поля
* заполняются от того, что редактируем, ровно один раз на открытие.
* */
export const CouponDialog = () => {
    const state = useCouponsContext();
    const coupon = state.dialog.kind === "editCoupon" ? state.dialog.coupon : null;

    // Валюта редактируемого промокода: она могла остаться прежней после того, как
    // заведение сменило свою.
    const couponCurrencyId = coupon && coupon.type === "FixedAmount" ? coupon.currencyId : null;

    const form = useCouponForm(coupon, state.formatterFor(couponCurrencyId));

    const submit = async () => {
        const values = form.buildValues();

        if (!values) return;

        await state.submitCoupon(values);
    };

    return (
        <Modal
            open
            onClose={state.closeDialog}
            title={coupon ? "Редактирование промокода" : "Новый промокод"}
            actions={
                <>
                    <Button
                        variant="outlined"
                        color="neutral"
                        disabled={state.saving}
                        onClick={state.closeDialog}
                    >
                        Отмена
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!form.canSubmit || state.saving}
                        onClick={submit}
                    >
                        {coupon ? "Сохранить" : "Добавить"}
                    </Button>
                </>
            }
        >
            <Stack $gap={16}>
                {form.error && <Banner tone="danger">{form.error}</Banner>}

                <LabeledField label="Код" hint="Его гость вводит в корзине" htmlFor="coupon-code">
                    <CodeRow>
                        <CodeField>
                            <TextInput
                                id="coupon-code"
                                value={form.code}
                                placeholder="SUMMER25"
                                disabled={state.saving}
                                onChange={form.setCode}
                            />
                        </CodeField>
                        <Button
                            variant="outlined"
                            color="neutral"
                            disabled={state.saving}
                            onClick={form.generateCode}
                        >
                            Сгенерировать
                        </Button>
                    </CodeRow>
                </LabeledField>

                <LabeledField
                    label="Тип скидки"
                    // Предупреждаем до того, как выбор станет необратимым; у сохранённого
                    // промокода о том же говорит заблокированный селект.
                    hint={form.isTypeLocked ? undefined : "После создания изменить нельзя"}
                    htmlFor="coupon-type"
                >
                    <Select
                        id="coupon-type"
                        value={form.type}
                        options={TYPE_OPTIONS}
                        disabled={state.saving || form.isTypeLocked}
                        onChange={(value) => form.setType(value as CouponTypeDto)}
                    />
                </LabeledField>

                <LabeledField
                    label={form.type === "Percentage" ? "Размер скидки" : "Сумма скидки"}
                    hint={form.type === "Percentage" ? "От 1 до 99 %" : undefined}
                    htmlFor="coupon-discount"
                >
                    <TextInput
                        id="coupon-discount"
                        value={form.discount}
                        suffix={form.type === "Percentage" ? "%" : state.symbolFor(couponCurrencyId)}
                        disabled={state.saving}
                        onChange={form.setDiscount}
                    />
                </LabeledField>

                <LabeledField
                    label="Количество использований"
                    hint={form.isUnlimited ? "0 — без ограничений" : undefined}
                    htmlFor="coupon-usages"
                >
                    <TextInput
                        id="coupon-usages"
                        value={form.totalUsagesLimit}
                        disabled={state.saving}
                        onChange={form.setTotalUsagesLimit}
                    />
                </LabeledField>

                <LabeledField label="Срок действия">
                    <Period>
                        <DateField
                            value={form.startDate}
                            max={form.endDate || undefined}
                            disabled={state.saving}
                            onChange={form.setStartDate}
                        />
                        <DateField
                            value={form.endDate}
                            min={form.startDate || undefined}
                            disabled={state.saving}
                            onChange={form.setEndDate}
                        />
                    </Period>
                </LabeledField>
            </Stack>
        </Modal>
    );
};
